import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import config from '../config/config';

const QUICKSAND = "https://fonts.googleapis.com/css2?family=Quicksand:wght@400;500;600;700&display=swap";

const theme = {
  green: "#22c55e", greenLight: "#dcfce7", greenDark: "#15803d",
  red: "#ef4444", redLight: "#fee2e2", redDark: "#b91c1c",
  yellow: "#f59e0b", yellowLight: "#fef3c7", yellowDark: "#b45309",
  blue: "#3b82f6", blueLight: "#dbeafe", blueDark: "#1d4ed8",
  purple: "#8b5cf6", purpleLight: "#ede9fe", purpleDark: "#6d28d9",
  teal: "#14b8a6", tealLight: "#ccfbf1", tealDark: "#0f766e",
  orange: "#f97316", orangeLight: "#ffedd5", orangeDark: "#c2410c",
  indigo: "#6366f1", indigoLight: "#e0e7ff", indigoDark: "#4338ca",
  pink: "#ec4899", pinkLight: "#fce7f3", pinkDark: "#be185d",
  cyan: "#06b6d4", cyanLight: "#cffafe", cyanDark: "#0e7490",
  gray50: "#f9fafb", gray100: "#f3f4f6", gray200: "#e5e7eb",
  gray400: "#9ca3af", gray600: "#4b5563", gray800: "#1f2937",
};

const styles = `
  @import url('${QUICKSAND}');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Quicksand', sans-serif; background: #f9fafb; }
  .app { max-width: 700px; margin: 0 auto; min-height: 100vh; background: #fff; position: relative; padding-bottom: 80px; }
  .screen { padding: 20px 16px; }

  @media (min-width: 768px) {
    body { background: #f1f5f9; }
    .app { max-width: 100%; display: flex; min-height: 100vh; }
    .app-sidebar { width: 220px; min-width: 220px; background: #fff; border-right: 1.5px solid ${theme.gray200}; display: flex; flex-direction: column; padding: 20px 0; position: sticky; top: 0; height: 100vh; overflow-y: auto; }
    .app-content { flex: 1; overflow-y: auto; padding-bottom: 0; background: #f8fafc; max-width: calc(100% - 220px); }
    .nav-bottom { display: none !important; }
    .app-header { position: sticky; top: 0; z-index: 30; }
    .screen { padding: 28px 32px; max-width: 900px; margin: 0 auto; }
  }
  @media (max-width: 767px) {
    .app-sidebar { display: none !important; }
    .app-content { width: 100%; }
  }

  .sidebar-logo { padding: 0 20px 24px; border-bottom: 1px solid ${theme.gray100}; margin-bottom: 8px; }
  .sidebar-logo-title { font-size: 17px; font-weight: 800; color: ${theme.gray800}; }
  .sidebar-logo-sub { font-size: 12px; color: ${theme.gray400}; font-weight: 600; margin-top: 2px; }
  .sidebar-nav-item { display: flex; align-items: center; gap: 12px; padding: 11px 20px; cursor: pointer; font-family: 'Quicksand', sans-serif; font-size: 14px; font-weight: 700; color: ${theme.gray600}; border: none; background: transparent; width: 100%; text-align: left; transition: all 0.15s; border-radius: 0; }
  .sidebar-nav-item:hover { background: ${theme.gray50}; color: ${theme.gray800}; }
  .sidebar-nav-item.active { background: ${theme.greenLight}; color: ${theme.greenDark}; border-right: 3px solid ${theme.green}; }
  .sidebar-nav-icon { width: 22px; height: 22px; flex-shrink: 0; }

  .btn-primary { background: ${theme.green}; color: #fff; border: none; border-radius: 14px; padding: 15px; font-family: 'Quicksand', sans-serif; font-size: 16px; font-weight: 700; cursor: pointer; width: 100%; transition: all 0.18s; display: flex; align-items: center; justify-content: center; gap: 8px; }
  .btn-primary:hover:not(:disabled) { background: ${theme.greenDark}; transform: translateY(-1px); box-shadow: 0 6px 20px rgba(34,197,94,0.25); }
  .btn-primary:active { transform: translateY(0); }
  .btn-primary:disabled { opacity: 0.65; cursor: not-allowed; }
  .btn-danger { background: ${theme.red}; color: #fff; border: none; border-radius: 14px; padding: 15px; font-family: 'Quicksand', sans-serif; font-size: 16px; font-weight: 700; cursor: pointer; width: 100%; transition: all 0.18s; display: flex; align-items: center; justify-content: center; gap: 8px; }
  .btn-danger:hover:not(:disabled) { background: ${theme.redDark}; transform: translateY(-1px); }
  .btn-danger:disabled { opacity: 0.65; cursor: not-allowed; }
  .btn-outline { background: transparent; color: ${theme.gray800}; border: 1.5px solid ${theme.gray200}; border-radius: 10px; padding: 9px 16px; font-family: 'Quicksand', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.15s; }
  .btn-outline:hover { border-color: ${theme.green}; color: ${theme.green}; background: ${theme.greenLight}; }
  .input-field { width: 100%; border: 1.5px solid ${theme.gray200}; border-radius: 12px; padding: 12px 14px; font-family: 'Quicksand', sans-serif; font-size: 15px; font-weight: 600; color: ${theme.gray800}; outline: none; transition: border 0.15s, box-shadow 0.15s; background: ${theme.gray50}; }
  .input-field:focus { border-color: ${theme.green}; background: #fff; box-shadow: 0 0 0 3px ${theme.greenLight}; }
  .label { font-size: 11px; font-weight: 700; color: ${theme.gray600}; margin-bottom: 5px; text-transform: uppercase; letter-spacing: 0.6px; }
  .card { background: #fff; border: 1.5px solid ${theme.gray200}; border-radius: 18px; padding: 18px; margin-bottom: 14px; }
  .card-colored { border-radius: 18px; padding: 18px; margin-bottom: 14px; }

  .nav-bottom { position: fixed; bottom: 0; left: 50%; transform: translateX(-50%); width: 100%; max-width: 430px; background: #fff; border-top: 1.5px solid ${theme.gray200}; display: flex; z-index: 100; padding: 6px 0 8px; }
  .nav-item { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 3px; cursor: pointer; padding: 4px 0; transition: all 0.15s; border: none; background: transparent; font-family: 'Quicksand', sans-serif; }
  .nav-icon { font-size: 22px; }
  .nav-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; }

  .pill { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 100px; font-size: 12px; font-weight: 700; }
  .tag-cat { padding: 9px 18px; border-radius: 100px; font-size: 13px; font-weight: 700; cursor: pointer; border: 1.5px solid transparent; transition: all 0.15s; white-space: nowrap; font-family: 'Quicksand', sans-serif; }
  .product-btn { border: 1.5px solid ${theme.gray200}; border-radius: 16px; padding: 16px 12px; text-align: center; cursor: pointer; transition: all 0.18s; background: #fff; font-family: 'Quicksand', sans-serif; }
  .product-btn:hover { border-color: ${theme.green}; box-shadow: 0 4px 16px rgba(34,197,94,0.12); transform: translateY(-2px); }
  .qty-btn { background: ${theme.gray100}; border: none; border-radius: 8px; padding: 8px 12px; font-family: 'Quicksand', sans-serif; font-size: 14px; font-weight: 700; cursor: pointer; transition: all 0.12s; color: ${theme.gray800}; }
  .qty-btn:hover { background: ${theme.greenLight}; color: ${theme.greenDark}; }
  .stat-card { border-radius: 14px; padding: 14px 12px; text-align: center; }
  .alert-box { border-radius: 12px; padding: 13px; margin-bottom: 12px; display: flex; align-items: flex-start; gap: 10px; }
  .section-header { font-size: 16px; font-weight: 800; color: ${theme.gray800}; margin-bottom: 14px; }
  .divider { height: 1px; background: ${theme.gray100}; margin: 18px 0; }
  .select-field { width: 100%; border: 1.5px solid ${theme.gray200}; border-radius: 12px; padding: 12px 14px; font-family: 'Quicksand', sans-serif; font-size: 15px; font-weight: 600; color: ${theme.gray800}; outline: none; transition: border 0.15s; background: ${theme.gray50}; appearance: none; cursor: pointer; }
  .select-field:focus { border-color: ${theme.green}; background: #fff; }
  .log-row { display: flex; align-items: center; gap: 12px; padding: 12px 0; border-bottom: 1px solid ${theme.gray100}; }
  .badge { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 15px; flex-shrink: 0; }
  @keyframes slideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
  .slide-up { animation: slideUp 0.3s ease forwards; }
  .toast { position: fixed; top: 20px; left: 50%; transform: translateX(-50%); background: ${theme.gray800}; color: #fff; padding: 11px 22px; border-radius: 100px; font-size: 13px; font-weight: 700; z-index: 999; font-family: 'Quicksand', sans-serif; white-space: nowrap; box-shadow: 0 8px 28px rgba(0,0,0,0.16); animation: slideUp 0.28s ease; }

  .date-filter-row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 20px; }
  .day-chip { padding: 7px 14px; border-radius: 100px; font-size: 13px; font-weight: 700; cursor: pointer; border: 1.5px solid ${theme.gray200}; background: #fff; color: ${theme.gray600}; font-family: 'Quicksand', sans-serif; transition: all 0.14s; white-space: nowrap; }
  .day-chip:hover { border-color: ${theme.green}; color: ${theme.greenDark}; background: ${theme.greenLight}; }
  .day-chip.active { background: ${theme.green}; color: #fff; border-color: ${theme.green}; }
  .date-input { border: 1.5px solid ${theme.gray200}; border-radius: 10px; padding: 7px 12px; font-family: 'Quicksand', sans-serif; font-size: 13px; font-weight: 600; color: ${theme.gray800}; outline: none; background: ${theme.gray50}; cursor: pointer; }
  .date-input:focus { border-color: ${theme.green}; background: #fff; }

  @media (min-width: 768px) {
    .endday-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; align-items: start; }
    .endday-summary-col { position: sticky; top: 20px; }
  }
  @media (max-width: 767px) {
    .endday-grid { display: block; }
  }

  .delta-badge { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 6px; font-size: 11px; font-weight: 700; margin-left: 8px; }
  .delta-pos { background: ${theme.greenLight}; color: ${theme.greenDark}; }
  .delta-neg { background: ${theme.redLight}; color: ${theme.redDark}; }
  .delta-zero { background: ${theme.gray100}; color: ${theme.gray600}; }

  .field-row { margin-bottom: 14px; }
  .field-label-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 5px; }

  @media (min-width: 768px) {
    .summary-stat-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 16px; }
    .summary-stat-grid .span2 { grid-column: span 2; }
  }
  @media (max-width: 767px) {
    .summary-stat-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 16px; }
  }

  .rich-stat-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px; }
  @media (min-width: 768px) {
    .rich-stat-grid { grid-template-columns: repeat(3, 1fr); }
    .rich-stat-grid .span2 { grid-column: span 2; }
    .rich-stat-grid .span3 { grid-column: span 3; }
  }
  .rich-stat-card { border-radius: 16px; padding: 16px 14px; position: relative; overflow: hidden; }
  .rich-stat-card::after { content: ''; position: absolute; right: -10px; bottom: -10px; width: 56px; height: 56px; border-radius: 50%; opacity: 0.12; background: currentColor; }
  .rich-stat-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px; opacity: 0.72; margin-bottom: 6px; }
  .rich-stat-value { font-size: 20px; font-weight: 800; }
  .rich-stat-sub { font-size: 11px; font-weight: 600; opacity: 0.6; margin-top: 3px; }

  .group-header { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; color: ${theme.gray400}; margin: 20px 0 10px; padding-bottom: 6px; border-bottom: 1.5px solid ${theme.gray100}; }

  @keyframes spin { to { transform: rotate(360deg); } }
  .spinner { width: 18px; height: 18px; border: 2.5px solid rgba(255,255,255,0.4); border-top-color: #fff; border-radius: 50%; animation: spin 0.7s linear infinite; flex-shrink: 0; }
  .spinner-dark { border-color: rgba(0,0,0,0.15); border-top-color: ${theme.gray800}; }

  .loader-overlay { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 20px; gap: 14px; }
  .loader-spinner-lg { width: 36px; height: 36px; border: 3px solid ${theme.gray200}; border-top-color: ${theme.green}; border-radius: 50%; animation: spin 0.8s linear infinite; }

  .audit-filter-chip { padding: 6px 14px; border-radius: 100px; border: 1.5px solid ${theme.gray200}; background: #fff; color: ${theme.gray600}; font-family: 'Quicksand', sans-serif; font-weight: 700; font-size: 12px; cursor: pointer; white-space: nowrap; transition: all 0.14s; }
  .audit-filter-chip:hover { border-color: ${theme.green}; color: ${theme.greenDark}; background: ${theme.greenLight}; }
  .audit-filter-chip.active { background: ${theme.gray800}; color: #fff; border-color: ${theme.gray800}; }
`;

const PRODUCTS = {
  RO: [
    { id: 1, name: "20L Purified", icon: "💧", price: 20, unit: "bottle" },
    { id: 2, name: "10L RO", icon: "🫗", price: 10, unit: "bottle" },
    { id: 3, name: "5L RO", icon: "🧴", price: 5, unit: "bottle" },
  ],
  Kiosk: [
    { id: 4, name: "Raw Water", icon: "🪣", price: 3, unit: "20L" },
    { id: 5, name: "Fresh Water", icon: "🌊", price: 5, unit: "20L" },
  ],
  Delivery: [
    { id: 6, name: "Delivery Fee", icon: "🛵", price: 100, unit: "trip" },
    { id: 7, name: "20L Delivered", icon: "📦", price: 120, unit: "bottle" },
  ],
};

const NAV_ICONS = {
  start: "https://img.icons8.com/fluency/48/circled-play.png",
  pos: "https://img.icons8.com/officel/80/pos-terminal.png",
  expense: "https://img.icons8.com/fluency/48/delete-receipt.png",
  endday: "https://img.icons8.com/officel/80/close-pane.png",
  summary: "https://img.icons8.com/fluency/48/graph-report.png",
  audit: "https://img.icons8.com/stencil/32/assignment-turned-in.png",
};

function Toast({ msg }) {
  return msg ? <div className="toast">✓ {msg}</div> : null;
}

function Loader({ text = "Loading..." }) {
  return (
    <div className="loader-overlay">
      <div className="loader-spinner-lg" />
      <div style={{ fontSize: 13, fontWeight: 700, color: theme.gray400 }}>{text}</div>
    </div>
  );
}

function DateFilterBar({ selectedDate, onDateChange }) {
  const today = new Date().toISOString().split('T')[0];

  const getOffsetDate = (offsetDays) => {
    const d = new Date(today);
    d.setDate(d.getDate() + offsetDays);
    return d.toISOString().split('T')[0];
  };

  const quickDays = [
    { label: "Today", value: today },
    { label: "Yesterday", value: getOffsetDate(-1) },
    { label: "2d ago", value: getOffsetDate(-2) },
    { label: "3d ago", value: getOffsetDate(-3) },
  ];

  return (
    <div className="date-filter-row">
      {quickDays.map(d => (
        <button
          key={d.value}
          className={`day-chip${selectedDate === d.value ? ' active' : ''}`}
          onClick={() => onDateChange(d.value)}
        >
          {d.label}
        </button>
      ))}
      <input
        type="date"
        className="date-input"
        value={selectedDate}
        max={today}
        onChange={e => onDateChange(e.target.value)}
        title="Pick a date"
      />
    </div>
  );
}

function DeltaBadge({ opening, closing }) {
  if (closing === undefined || closing === null || closing === '') return null;
  const open = parseFloat(opening) || 0;
  const close = parseFloat(closing) || 0;
  const diff = close - open;
  if (diff === 0) return <span className="delta-badge delta-zero">= 0</span>;
  if (diff > 0) return <span className="delta-badge delta-pos">▲ {diff.toLocaleString()}</span>;
  return <span className="delta-badge delta-neg">▼ {Math.abs(diff).toLocaleString()}</span>;
}

async function fetchShiftForDate(date) {
  const token = localStorage.getItem('token');
  const res = await axios.get(`${config.API_URL}/api/shiftData/${date}`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
}

async function getXsrfToken() {
  await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });
  const match = document.cookie.match(new RegExp("(^| )XSRF-TOKEN=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

function StartDayScreen() {
  const today = new Date().toISOString().split('T')[0];
  const [fields, setFields] = useState({
    ro: "",
    ro_price: "",
    ro_inlet: "",
    ro_purified: "",
    opening_mpesa: "",
    raw: "",
    power: "",
    cash: "",
    outlet: "",
    inlet: "",
    fresh: "",
    cleaning: "",
  });
  const [errors, setErrors] = useState({});
  const [shiftDatas, setShiftData] = useState({});
  const [selectedDate, setSelectedDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  const showToast = (msg) => { setToastMsg(msg); setTimeout(() => setToastMsg(""), 2500); };

  const loadShiftData = async (date) => {
    setLoading(true);
    try {
      const data = await fetchShiftForDate(date);
      const sd = data.shiftData || {};
      setShiftData(sd);
      setFields({
        ro: sd?.ro_taps_opening ?? "",
        ro_inlet: sd?.ro_raw_inlet_opening ?? "",
        ro_purified: sd?.ro_purified_inlet_opening ?? "",
        ro_price: sd?.ro_price ?? "",
        opening_mpesa: sd?.opening_mpesa ?? "",
        raw: sd?.kiosk_raw_opening ?? "",
        power: sd?.power_meter ?? "",
        outlet: sd?.ro_kiosk_outlet_opening ?? "",
        inlet: sd?.ro_kiosk_inlet_opening ?? "",
        fresh: sd?.kiosk_fresh_opening ?? "",
        cleaning: sd?.kiosk_cleaning_opening ?? "",
        cash: sd?.opening_cash ?? "",
      });
    } catch (e) {
      console.error("Failed to load shift data:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadShiftData(selectedDate); }, [selectedDate]);

  const validate = () => {
    const e = {};
    ['ro', 'ro_purified', 'ro_inlet', 'ro_price', 'opening_mpesa', 'raw', 'power', 'outlet', 'inlet', 'fresh', 'cleaning', 'cash'].forEach(k => {
      if (fields[k] === "" || fields[k] === null || fields[k] === undefined) e[k] = "Required";
    });
    return e;
  };

  const handle = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSubmitting(true);
    try {
      const xsrfToken = await getXsrfToken();
      await axios.post(config.API_URL + "/api/startShift", fields, {
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-XSRF-TOKEN": xsrfToken },
        withCredentials: true,
      });
      showToast("Shift started successfully!");
      await loadShiftData(selectedDate);
    } catch (error) {
      showToast("Failed to start shift. Try again.");
      console.error("Start shift error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const now = new Date(selectedDate + 'T12:00:00');
  const dateStr = now.toLocaleDateString("en-KE", { weekday: "long", day: "numeric", month: "long" });

  const formFields = [
    { key: "ro", label: "RO - Taps (Opening)", figure: shiftDatas?.ro_taps_opening },
    { key: "ro_inlet", label: "RO - Raw Inlet (Opening)", figure: shiftDatas?.ro_raw_inlet_opening },
    { key: "ro_purified", label: "RO - Purified Inlet (Opening)", figure: shiftDatas?.ro_purified_inlet_opening },
    { key: "ro_price", label: "RO - Pricing", figure: shiftDatas?.ro_price },
    { key: "raw", label: "Kiosk Raw Opening", figure: shiftDatas?.kiosk_raw_opening },
    { key: "cleaning", label: "Kiosk Cleaning Opening", figure: shiftDatas?.kiosk_cleaning_opening },
    { key: "fresh", label: "Kiosk Fresh Opening", figure: shiftDatas?.kiosk_fresh_opening },
    /* ro_kiosk_outlet_opening — commented out as requested */
    // { key: "outlet", label: "RO Kiosk Outlet Opening", figure: shiftDatas?.ro_kiosk_outlet_opening },
    { key: "inlet", label: "RO Kiosk Inlet Opening", figure: shiftDatas?.ro_kiosk_inlet_opening },
    { key: "power", label: "Power Meter", figure: shiftDatas?.power_meter },
    { key: "cash", label: "Opening Cash (KES)", figure: shiftDatas?.opening_cash },
    { key: "opening_mpesa", label: "Opening Mpesa (KES)", figure: shiftDatas?.opening_mpesa },
  ];

  return (
    <div className="screen slide-up">
      <Toast msg={toastMsg} />
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: theme.gray800, marginBottom: 2 }}>Start Shift</div>
        <div style={{ fontSize: 13, color: theme.gray400, fontWeight: 600 }}>{dateStr}</div>
      </div>

      <DateFilterBar selectedDate={selectedDate} onDateChange={setSelectedDate} />

      <div style={{ background: theme.greenLight, borderRadius: 16, padding: "14px 18px", marginBottom: 22, display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 36, height: 36, background: theme.green, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: '#fff', flexShrink: 0 }}></div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.greenDark }}>Opening Readings</div>
          <div style={{ fontSize: 12, color: theme.greenDark, opacity: 0.75 }}>Enter meter readings to begin tracking</div>
        </div>
      </div>

      {loading ? (
        <Loader text="Loading shift data..." />
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "0 20px" }}>
            {formFields.map(f => (
              <div key={f.key} className="field-row">
                <div className="label">
                  {f.label} <span style={{ color: theme.red }}>*</span>
                </div>
                <input
                  className="input-field"
                  type="number"
                  placeholder={f.figure !== undefined && f.figure !== null ? String(f.figure) : "0"}
                  value={fields[f.key]}
                  onChange={e => {
                    setFields(p => ({ ...p, [f.key]: e.target.value }));
                    setErrors(p => ({ ...p, [f.key]: "" }));
                  }}
                  style={errors[f.key] ? { borderColor: theme.red, background: theme.redLight } : {}}
                />
                {errors[f.key] && (
                  <div style={{ fontSize: 11, color: theme.red, fontWeight: 700, marginTop: 3 }}>{errors[f.key]}</div>
                )}
              </div>
            ))}
          </div>

          <div style={{ marginTop: 24 }}>
            <button className="btn-primary" onClick={handle} disabled={submitting}>
              {submitting ? <><div className="spinner" /> Starting Shift...</> : "▶ Start Shift"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function POSScreen() {
  const [cat, setCat] = useState("RO");
  const [cart, setCart] = useState([]);
  const [payment, setPayment] = useState("Cash");
  const [toastMsg, setToastMsg] = useState("");
  const [lastSale, setLastSale] = useState(null);
  const [showQty, setShowQty] = useState(null);

  const showToast = (msg) => { setToastMsg(msg); setTimeout(() => setToastMsg(""), 2200); };

  const addToCart = (product, qty = 1) => {
    setCart(prev => {
      const ex = prev.find(i => i.id === product.id);
      if (ex) return prev.map(i => i.id === product.id ? { ...i, qty: i.qty + qty } : i);
      return [...prev, { ...product, qty }];
    });
    setShowQty(null);
  };

  const removeItem = (id) => setCart(p => p.filter(i => i.id !== id));
  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);

  const completeSale = () => {
    if (!cart.length) { showToast("Add items first"); return; }
    setLastSale([...cart]);
    setCart([]);
    showToast(`Sale of KES ${total} complete!`);
  };

  const repeatLast = () => {
    if (lastSale) { setCart([...lastSale]); showToast("Last sale repeated"); }
  };

  return (
    <div className="slide-up">
      <Toast msg={toastMsg} />

      <div style={{ display: "flex", gap: 8, padding: "14px 16px 0", overflowX: "auto", paddingBottom: 4 }}>
        {Object.keys(PRODUCTS).map(c => (
          <button key={c} className="tag-cat" onClick={() => setCat(c)}
            style={{ background: cat === c ? theme.green : theme.gray100, color: cat === c ? "#fff" : theme.gray600, borderColor: cat === c ? theme.green : "transparent" }}>
            {c === "RO" ? "" : c === "Kiosk" ? "" : ""} {c}
          </button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12, padding: "14px 16px" }}>
        {PRODUCTS[cat].map(p => (
          <div key={p.id}>
            <div
              className="product-btn"
              onClick={() => setShowQty(showQty === p.id ? null : p.id)}
              style={cart.find(i => i.id === p.id) ? { borderColor: theme.green, background: theme.greenLight } : {}}
            >
              <div style={{ fontSize: 28, marginBottom: 6 }}>{p.icon}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: theme.gray800, lineHeight: 1.3 }}>{p.name}</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: theme.green, marginTop: 4 }}>KES {p.price}</div>
              <div style={{ fontSize: 11, color: theme.gray400, fontWeight: 600 }}>per {p.unit}</div>
              {cart.find(i => i.id === p.id) && (
                <div className="pill" style={{ background: theme.green, color: "#fff", marginTop: 8, justifyContent: "center" }}>
                  x{cart.find(i => i.id === p.id).qty} in cart
                </div>
              )}
            </div>
            {showQty === p.id && (
              <div style={{ background: theme.gray50, borderRadius: 12, padding: 10, marginTop: 6, border: `1.5px solid ${theme.green}` }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: theme.gray600, marginBottom: 6 }}>QTY</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[1, 2, 5, 10].map(q => (
                    <button key={q} className="qty-btn" onClick={() => addToCart(p, q)}>+{q}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {cart.length > 0 && (
        <div className="card" style={{ margin: "0 16px 12px", border: `2px solid ${theme.green}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: theme.gray800 }}>🛒 Cart</div>
            <button className="btn-outline" onClick={() => setCart([])} style={{ padding: "4px 12px", fontSize: 12, color: theme.red, borderColor: theme.red }}>Clear</button>
          </div>
          {cart.map(item => (
            <div key={item.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 8, marginBottom: 8, borderBottom: `1px solid ${theme.gray100}` }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{item.icon} {item.name}</span>
                <span style={{ fontSize: 12, color: theme.gray400, marginLeft: 6 }}>x{item.qty}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontWeight: 800, color: theme.green, fontSize: 14 }}>KES {item.price * item.qty}</span>
                <button onClick={() => removeItem(item.id)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, color: theme.red }}>✕</button>
              </div>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 4 }}>
            <span style={{ fontWeight: 700, color: theme.gray600, fontSize: 14 }}>Total</span>
            <span style={{ fontWeight: 800, fontSize: 20, color: theme.gray800 }}>KES {total}</span>
          </div>
        </div>
      )}

      <div style={{ padding: "0 16px" }}>
        <div className="label" style={{ marginBottom: 8 }}>Payment Method</div>
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          {["Cash", "M-Pesa"].map(m => (
            <button
              key={m}
              onClick={() => setPayment(m)}
              style={{
                flex: 1, padding: "12px",
                border: `2px solid ${payment === m ? theme.green : theme.gray200}`,
                borderRadius: 12,
                background: payment === m ? theme.greenLight : "#fff",
                fontFamily: "'Quicksand', sans-serif",
                fontWeight: 700, fontSize: 14, cursor: "pointer",
                color: payment === m ? theme.greenDark : theme.gray600,
                transition: "all 0.15s"
              }}
            >
              {m === "Cash" ? "" : ""} {m}
            </button>
          ))}
        </div>
        <button className="btn-primary" onClick={completeSale} style={{ marginBottom: 10 }}>
          ✓ Complete Sale {cart.length > 0 && `— KES ${total}`}
        </button>
        {lastSale && (
          <button className="btn-outline" onClick={repeatLast} style={{ width: "100%", padding: "12px" }}>
            Repeat Last Sale
          </button>
        )}
      </div>
    </div>
  );
}

function ExpenseScreen() {
  const today = new Date().toISOString().split('T')[0];
  const [amount, setAmount] = useState("");
  const [cat, setCat] = useState("");
  const [desc, setDesc] = useState("");
  const [toastMsg, setToastMsg] = useState("");
  const [selectedDate, setSelectedDate] = useState(today);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [recentExpenses, setRecentExpenses] = useState([]);
  const [loading, setLoading] = useState(false);

  const showToast = (msg) => { setToastMsg(msg); setTimeout(() => setToastMsg(""), 2500); };

  const loadData = async (date) => {
    setLoading(true);
    try {
      const data = await fetchShiftForDate(date);
      setExpenseCategories(Array.isArray(data?.expenseCategories) ? data.expenseCategories : []);
      setRecentExpenses(Array.isArray(data?.recent) ? data.recent : []);
    } catch (e) {
      console.error("Failed to load expense data:", e);
      setExpenseCategories([]);
      setRecentExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(selectedDate); }, [selectedDate]);

  const save = () => {
    if (!amount || !cat) return;
    setAmount(""); setCat(""); setDesc("");
    showToast("Expense saved!");
  };

  return (
    <div className="screen slide-up">
      <Toast msg={toastMsg} />
      <div style={{ fontSize: 22, fontWeight: 800, color: theme.gray800, marginBottom: 2 }}>Record Expense</div>
      <div style={{ fontSize: 13, color: theme.gray400, fontWeight: 600, marginBottom: 18 }}>Log operational costs</div>

      <DateFilterBar selectedDate={selectedDate} onDateChange={setSelectedDate} />

      {loading ? (
        <Loader text="Loading expense data..." />
      ) : (
        <>
          <div className="card" style={{ border: `1.5px solid ${theme.red}30` }}>
            <div className="label">Amount (KES) <span style={{ color: theme.red }}>*</span></div>
            <input
              className="input-field"
              type="number"
              placeholder="0"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              style={{ fontSize: 26, fontWeight: 800, textAlign: "center", marginBottom: 10 }}
            />
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {[100, 200, 500, 1000].map(a => (
                <button key={a} className="qty-btn" onClick={() => setAmount(String(a))} style={{ flex: 1, fontSize: 13 }}>{a}</button>
              ))}
            </div>
            <div className="label" style={{ marginTop: 4 }}>Category <span style={{ color: theme.red }}>*</span></div>
            <select className="select-field" value={cat} onChange={e => setCat(e.target.value)} style={{ marginBottom: 12 }}>
              <option value="">Select category...</option>
              {expenseCategories.map(c => (
                <option key={c?.id ?? c} value={c?.name ?? c}>{c?.name ?? c}</option>
              ))}
            </select>
            <div className="label">Description (optional)</div>
            <input
              className="input-field"
              placeholder="Brief note..."
              value={desc}
              onChange={e => setDesc(e.target.value)}
            />
          </div>

          <button
            className="btn-primary"
            onClick={save}
            style={{ background: !amount || !cat ? theme.gray400 : theme.red, marginBottom: 20 }}
            disabled={!amount || !cat}
          >
            💾 Save Expense
          </button>

          {Array.isArray(recentExpenses) && recentExpenses.length > 0 && (
            <>
              <div className="section-header" style={{ fontSize: 14 }}>Recent Expenses</div>
              {recentExpenses.map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 0", borderBottom: `1px solid ${theme.gray100}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 34, height: 34, background: theme.redLight, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>🧾</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: theme.gray800 }}>{r?.cat ?? "—"}</div>
                      <div style={{ fontSize: 11, color: theme.gray400, fontWeight: 600 }}>{r?.time ?? ""}</div>
                    </div>
                  </div>
                  <div style={{ fontWeight: 800, fontSize: 15, color: theme.red }}>KES {r?.amount ?? 0}</div>
                </div>
              ))}
            </>
          )}

          {Array.isArray(recentExpenses) && recentExpenses.length === 0 && (
            <div style={{ textAlign: "center", padding: "30px 0", color: theme.gray400, fontSize: 13, fontWeight: 600 }}>
              No expenses recorded for this date.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function EndDayScreen() {
  const today = new Date().toISOString().split('T')[0];
  const [inputs, setInputs] = useState({
    roClose: "",
    roInletClose: "",
    roPurifiedClose: "",
    rawClose: "",
    powerClose: "",
    inletClose: "",
    /* ro_kiosk_outlet_closing — commented out as requested */
    // outletClose: "",
    freshClose: "",
    cleanClose: "",
    cashHand: "",
    mpesa: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(today);
  const [shiftDatas, setShiftData] = useState({});
  const [loading, setLoading] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  const showToast = (msg) => { setToastMsg(msg); setTimeout(() => setToastMsg(""), 2500); };

  const fetchShift = async (date) => {
    setLoading(true);
    try {
      const data = await fetchShiftForDate(date);
      setShiftData(data?.shiftData || {});
      setSubmitted(false);
    } catch (e) {
      console.error("Failed to load shift:", e);
      setShiftData({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchShift(selectedDate); }, [selectedDate]);

  const set = (k, v) => setInputs(p => ({ ...p, [k]: v }));

  const opens = {
    ro: parseFloat(shiftDatas?.ro_taps_opening ?? 0) || 0,
    ro_inlet: parseFloat(shiftDatas?.ro_raw_inlet_opening ?? 0) || 0,
    ro_purified: parseFloat(shiftDatas?.ro_raw_purified_opening ?? 0) || 0,
    ro_price: parseFloat(shiftDatas?.ro_price ?? 0) || 0,
    opening_mpesa: parseFloat(shiftDatas?.opening_mpesa ?? 0) || 0,
    rolitressold: parseFloat(shiftDatas?.rolitressold ?? 0) || 0,
    powerusageksh: parseFloat(shiftDatas?.powerusageksh ?? 0) || 0,
    expectedCash: parseFloat(shiftDatas?.expectedCash ?? 0) || 0,
    mpesaCash: parseFloat(shiftDatas?.mpesaCash ?? 0) || 0,
    powerusagekwh: parseFloat(shiftDatas?.powerusagekwh ?? 0) || 0,
    raw: parseFloat(shiftDatas?.kiosk_raw_opening ?? 0) || 0,
    power: parseFloat(shiftDatas?.power_meter ?? 0) || 0,
    /* ro_kiosk_outlet_opening — commented out as requested */
    // outlet: parseFloat(shiftDatas?.ro_kiosk_outlet_opening ?? 0) || 0,
    inlet: parseFloat(shiftDatas?.ro_kiosk_inlet_opening ?? 0) || 0,
    cleaning: parseFloat(shiftDatas?.kiosk_cleaning_opening ?? 0) || 0,
    fresh: parseFloat(shiftDatas?.kiosk_fresh_opening ?? 0) || 0,
    cash: parseFloat(shiftDatas?.opening_cash ?? 0) || 0,
  };

  const systemSales = parseFloat(shiftDatas?.mpesa ?? 0) || 0;

  const roClose = parseFloat(inputs.roClose) || 0;
  const roInletClose = parseFloat(inputs.roInletClose) || 0;
  const roPurifiedClose = parseFloat(inputs.roPurifiedClose) || 0;
  const inletClose = parseFloat(inputs.inletClose) || 0;
  /* ro_kiosk_outlet_closing — commented out as requested */
  // const outletClose = parseFloat(inputs.outletClose) || 0;
  const freshClose = parseFloat(inputs.freshClose) || 0;
  const cleanClose = parseFloat(inputs.cleanClose) || 0;
  const powerClose = parseFloat(inputs.powerClose) || 0;
  const rawClose = parseFloat(inputs.rawClose) || 0;
  const cashHand = parseFloat(inputs.cashHand) || 0;
  const mpesa = parseFloat(inputs.mpesa) || 0;

  const litresProd = roClose > opens.ro ? (roClose - opens.ro) * 1000 : 0;
  const rolitressold = roClose > opens.ro ? (roClose - opens.ro) * 1000 : 0;
  const rolitressoldactual = opens.rolitressold;
  const kioskraw = rawClose > opens.ro_inlet ? (rawClose - opens.ro_inlet) * 1000 : 0;
  const powerusagekwh = opens.powerusagekwh;
  const powerusageksh = opens.powerusageksh;
  const kioskcleaning = cleanClose > opens.cleaning ? (cleanClose - opens.cleaning) * 1000 : 0;
  const rorawinlet = roInletClose > opens.ro_inlet ? (roInletClose - opens.ro_inlet) * 1000 : 0;
  const ropurifiedinlet = roPurifiedClose > opens.ro_purified ? (roPurifiedClose - opens.ro_purified) * 1000 : 0;
  const kioskfresh = freshClose > opens.fresh ? (freshClose - opens.fresh) * 1000 : 0;

  const rolitresProd = roClose > opens.ro ? (roClose - opens.ro) * 20 : 0;
  const litresSold = 62 * 20;
  const deviation = litresProd - litresSold;
  const expectedCash = opens.expectedCash;
  const actualCash = opens.mpesaCash;
  const cashDiff = actualCash - expectedCash;
  const powerUsed = powerClose ? powerClose - opens.power : 0;

  const hasWaterLoss = deviation < -100;
  const hasCashMismatch = Math.abs(cashDiff) > 50;

  const closeDay = async () => {
    setSubmitting(true);
    try {
      const xsrfToken = await getXsrfToken();
      await axios.post(
        config.API_URL + "/api/endShift",
        {
          roClose,
          roInletClose,
          roPurifiedClose,
          rawClose,
          freshClose,
          powerClose,
          cleanClose,
          inletClose,
          /* ro_kiosk_outlet_closing — commented out as requested */
          // outletClose,
          cashHand,
          mpesa,
          opening_slug: selectedDate,
        },
        { headers: { "Content-Type": "application/json", Accept: "application/json", "X-XSRF-TOKEN": xsrfToken }, withCredentials: true }
      );
      setSubmitted(true);
      showToast("Shift closed successfully!");
    } catch (error) {
      showToast("Failed to close shift. Try again.");
      console.error("End shift error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const meterFields = [
    { key: "roClose", label: "RO Taps (Closing)", icon: "", openKey: "ro" },
    { key: "roInletClose", label: "RO Raw Inlet (Closing)", icon: "", openKey: "ro_inlet" },
    { key: "roPurifiedClose", label: "RO Raw Purified (Closing)", icon: "", openKey: "ro_purified" },
    { key: "rawClose", label: "Kiosk Raw Closing", icon: "", openKey: "raw" },
    { key: "cleanClose", label: "Kiosk Cleaning Closing", icon: "", openKey: "cleaning" },
    { key: "freshClose", label: "Kiosk Fresh Closing", icon: "", openKey: "fresh" },
    /* ro_kiosk_outlet_closing — commented out as requested */
    // { key: "outletClose", label: "RO Kiosk Outlet Closing", icon: "", openKey: "outlet" },
    { key: "inletClose", label: "RO Kiosk Inlet Closing", icon: "", openKey: "inlet" },
    { key: "powerClose", label: "Power Meter", icon: "", openKey: "power" },
  ];

  const summaryStats = [
    { label: "RO litres sold", value: `${rolitressold.toLocaleString()}L`, bg: theme.blueLight, color: theme.blueDark },
    { label: "RO litres sold actual", value: `${rolitressoldactual.toLocaleString()}L`, bg: theme.indigoLight, color: theme.indigoDark },
    { label: "RO raw inlet litres", value: `${rorawinlet.toLocaleString()}L`, bg: theme.cyanLight, color: theme.cyanDark },
    { label: "RO purified litres", value: `${ropurifiedinlet.toLocaleString()}L`, bg: theme.greenLight, color: theme.greenDark },
    { label: "Kiosk raw litres", value: `${kioskraw.toLocaleString()}L`, bg: theme.tealLight, color: theme.tealDark },
    { label: "Kiosk cleaning litres", value: `${kioskcleaning.toLocaleString()}L`, bg: theme.purpleLight, color: theme.purpleDark },
    { label: "Kiosk fresh litres", value: `${kioskfresh.toLocaleString()}L`, bg: theme.yellowLight, color: theme.yellowDark },
    { label: "Total Litres Used", value: `${rolitressoldactual.toLocaleString()}L`, bg: theme.orangeLight, color: theme.orangeDark },
    { label: "Power Used", value: `${powerusagekwh.toFixed(0)} kWh`, bg: theme.yellowLight, color: theme.yellowDark },
    { label: "Power Cost", value: `KES ${powerusageksh.toFixed(0)}`, bg: theme.pinkLight, color: theme.pinkDark },
    { label: "Collected Cash", value: `KES ${expectedCash.toLocaleString()}`, bg: theme.greenLight, color: theme.greenDark },
    { label: "Mpesa Cash", value: `KES ${actualCash.toLocaleString()}`, bg: theme.tealLight, color: theme.tealDark },
  ];

  return (
    <div className="screen slide-up">
      <Toast msg={toastMsg} />
      <div style={{ fontSize: 22, fontWeight: 800, color: theme.gray800, marginBottom: 2 }}>Close Shift</div>
      <div style={{ fontSize: 13, color: theme.gray400, fontWeight: 600, marginBottom: 18 }}>End-of-day reconciliation</div>

      <DateFilterBar selectedDate={selectedDate} onDateChange={setSelectedDate} />

      {loading ? (
        <Loader text="Loading shift data..." />
      ) : (
        <>
          {(hasWaterLoss || hasCashMismatch) && (
            <div style={{ marginBottom: 16 }}>
              {hasWaterLoss && (
                <div className="alert-box" style={{ background: theme.redLight, border: `1.5px solid ${theme.red}40` }}>
                  <span style={{ fontSize: 18 }}></span>
                  <div>
                    <div style={{ fontWeight: 800, color: theme.redDark, fontSize: 13 }}>Water Loss Detected</div>
                    <div style={{ fontSize: 12, color: theme.redDark, opacity: 0.85 }}>{Math.abs(deviation)}L unaccounted for</div>
                  </div>
                </div>
              )}
              {hasCashMismatch && (
                <div className="alert-box" style={{ background: theme.yellowLight, border: `1.5px solid ${theme.yellow}40` }}>
                  <span style={{ fontSize: 18 }}></span>
                  <div>
                    <div style={{ fontWeight: 800, color: theme.yellowDark, fontSize: 13 }}>Cash Mismatch</div>
                    <div style={{ fontSize: 12, color: theme.yellowDark, opacity: 0.85 }}>Difference of KES {Math.abs(cashDiff).toFixed(0)}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="endday-grid">
            <div className="endday-inputs-col">
              <div className="card" style={{ background: theme.blueLight, border: `1.5px solid ${theme.blue}25` }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: theme.blueDark, marginBottom: 14 }}>Meter Readings</div>
                <div>
                  {meterFields.map(f => {
                    const openVal = opens[f.openKey];
                    return (
                      <div key={f.key} className="field-row">
                        <div className="field-label-row">
                          <div className="label" style={{ color: theme.blueDark }}>
                            {f.icon} {f.label}
                            <span style={{ fontWeight: 500, fontSize: 11, color: theme.gray400, marginLeft: 6, textTransform: "none", letterSpacing: 0 }}>
                              opened: {openVal}
                            </span>
                          </div>
                          <DeltaBadge opening={openVal} closing={inputs[f.key]} />
                        </div>
                        <input
                          className="input-field"
                          type="number"
                          placeholder={String(openVal)}
                          value={inputs[f.key]}
                          onChange={e => set(f.key, e.target.value)}
                          style={{ background: "#fff" }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="card" style={{ background: theme.greenLight, border: `1.5px solid ${theme.green}25` }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: theme.greenDark, marginBottom: 14 }}>Cash Reconciliation</div>
                {[
                  { key: "cashHand", label: "Cash in Hand (KES)", icon: "", openVal: opens.cash },
                  { key: "mpesa", label: "M-Pesa Total (KES)", icon: "", openVal: 0 },
                ].map(f => (
                  <div key={f.key} className="field-row">
                    <div className="field-label-row">
                      <div className="label" style={{ color: theme.greenDark }}>{f.icon} {f.label}</div>
                      <DeltaBadge opening={f.openVal} closing={inputs[f.key]} />
                    </div>
                    <input
                      className="input-field"
                      type="number"
                      placeholder="0"
                      value={inputs[f.key]}
                      onChange={e => set(f.key, e.target.value)}
                      style={{ background: "#fff" }}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="endday-summary-col">
              <div style={{ fontWeight: 800, fontSize: 15, color: theme.gray800, marginBottom: 12 }}>System Summary</div>
              <div className="summary-stat-grid">
                {summaryStats.map((s, i) => (
                  <div key={i} className="stat-card" style={{ background: s.bg, gridColumn: s.span ? 'span 2' : undefined }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: s.color, opacity: 0.72, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>{s.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
                  </div>
                ))}
              </div>

              <button className="btn-danger" onClick={closeDay} disabled={submitting || submitted} style={{ marginTop: 4 }}>
                {submitting
                  ? <><div className="spinner" /> Closing Shift...</>
                  : submitted
                    ? "✓ Shift Closed!"
                    : " Close Shift"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryScreen() {
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(today);
  const [stats, setStats] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shiftDatas, setShiftData] = useState({});

  const loadSummary = async (date) => {
    setLoading(true);
    try {
      const data = await fetchShiftForDate(date);
      setStats(Array.isArray(data?.stats) ? data.stats : []);
      setProducts(Array.isArray(data?.products) ? data.products : []);
      setShiftData(data?.shiftData || {});
    } catch (e) {
      console.error("Failed to load summary:", e);
      setStats([]);
      setProducts([]);
      setShiftData({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSummary(selectedDate); }, [selectedDate]);

  const resolveThemeVal = (val) => {
    if (!val) return val;
    if (typeof val === 'string' && val.startsWith("theme.")) {
      const key = val.replace("theme.", "");
      return theme[key] ?? val;
    }
    return val;
  };

  const sd = shiftDatas;
  const f = (v) => parseFloat(v ?? 0) || 0;

  const rolitressoldActual = f(sd?.rolitressoldactual);
  const powerusagekwh = f(sd?.powerusagekwh);
  const powerusageksh = f(sd?.powerusageksh);
  const expectedCash = f(sd?.expectedCash);
  const actualCash = f(sd?.actualCash);
  const openingCash = f(sd?.opening_cash);
  const openingMpesa = f(sd?.opening_mpesa);
  const mpesa = f(sd?.mpesa);

  const roTaps = f(sd?.ro_taps_opening);
  const kioskRaw = f(sd?.kiosk_raw_opening);
  const kioskFresh = f(sd?.kiosk_fresh_opening);
  const kioskCleaning = f(sd?.kiosk_cleaning_opening);
  /* ro_kiosk_outlet_opening — commented out as requested */
  // const roOutlet = f(sd?.ro_kiosk_outlet_opening);
  const roInlet = f(sd?.ro_kiosk_inlet_opening);
  const roRawInlet = f(sd?.ro_raw_inlet_opening);
  const roPurifiedInlet = f(sd?.ro_purified_inlet_opening);
  const powerMeter = f(sd?.power_meter);
  const roPrice = f(sd?.ro_price);

  const dateLabel = new Date(selectedDate + 'T12:00:00').toLocaleDateString("en-KE", {
    weekday: "long", day: "numeric", month: "long", year: "numeric"
  });

  const hasShiftData = sd && Object.keys(sd).length > 0;

  const salesStats = stats.map((s) => ({
    label: s.label,
    value: typeof s.value === 'number'
      ? (s.label?.toLowerCase().includes('sale') || s.label?.toLowerCase().includes('expense') || s.label?.toLowerCase().includes('cash')
        ? `KES ${Number(s.value).toLocaleString()}`
        : s.value)
      : s.value,
    bg: resolveThemeVal(s.icon),
    color: resolveThemeVal(s.color),
  }));

  const financialCards = [
    { label: "Opening Cash", value: `KES ${openingCash.toLocaleString()}`, bg: theme.greenLight, color: theme.greenDark },
    { label: "Opening M-Pesa", value: `KES ${openingMpesa.toLocaleString()}`, bg: theme.tealLight, color: theme.tealDark },
    { label: "M-Pesa Collected", value: `KES ${mpesa.toLocaleString()}`, bg: theme.indigoLight, color: theme.indigoDark },
    { label: "Collected Cash", value: `KES ${expectedCash.toLocaleString()}`, bg: theme.blueLight, color: theme.blueDark },
    { label: "Actual M-Pesa", value: `KES ${actualCash.toLocaleString()}`, bg: theme.cyanLight, color: theme.cyanDark },
  ];

  const roCards = [
    { label: "RO Litres Sold (Actual)", value: `${rolitressoldActual.toLocaleString()} L`, bg: theme.blueLight, color: theme.blueDark },
    { label: "RO Taps Opening", value: roTaps.toLocaleString(), bg: theme.indigoLight, color: theme.indigoDark },
    { label: "RO Raw Inlet Opening", value: `${roRawInlet.toLocaleString()} m³`, bg: theme.cyanLight, color: theme.cyanDark },
    { label: "RO Purified Inlet Opening", value: `${roPurifiedInlet.toLocaleString()} m³`, bg: theme.greenLight, color: theme.greenDark },
    /* ro_kiosk_outlet_opening — commented out as requested */
    // { label: "RO Kiosk Outlet Opening", value: roOutlet.toLocaleString(), bg: theme.tealLight, color: theme.tealDark },
    { label: "RO Kiosk Inlet Opening", value: roInlet.toLocaleString(), bg: theme.purpleLight, color: theme.purpleDark },
    { label: "RO Price", value: `KES ${roPrice}/unit`, bg: theme.orangeLight, color: theme.orangeDark },
  ];

  const kioskCards = [
    { label: "Kiosk Raw Opening", value: `${kioskRaw.toLocaleString()} m³`, bg: theme.tealLight, color: theme.tealDark },
    { label: "Kiosk Fresh Opening", value: `${kioskFresh.toLocaleString()} m³`, bg: theme.greenLight, color: theme.greenDark },
    { label: "Kiosk Cleaning Opening", value: `${kioskCleaning.toLocaleString()} m³`, bg: theme.purpleLight, color: theme.purpleDark },
  ];

  const powerCards = [
    { label: "Power Meter Opening", value: `${powerMeter.toLocaleString()} kWh`, bg: theme.yellowLight, color: theme.yellowDark },
    { label: "Power Used (kWh)", value: `${powerusagekwh.toFixed(2)} kWh`, bg: theme.orangeLight, color: theme.orangeDark },
    { label: "Power Cost", value: `KES ${powerusageksh.toFixed(0)}`, bg: theme.pinkLight, color: theme.pinkDark },
  ];

  const StatGroup = ({ title, cards }) => (
    <>
      <div className="group-header">{title}</div>
      <div className="rich-stat-grid" style={{ marginBottom: 4 }}>
        {cards.map((c, i) => (
          <div key={i} className="rich-stat-card" style={{ background: c.bg, color: c.color }}>
            <div className="rich-stat-label">{c.label}</div>
            <div className="rich-stat-value">{c.value ?? "—"}</div>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="screen slide-up">
      <div style={{ fontSize: 22, fontWeight: 800, color: theme.gray800, marginBottom: 2 }}>Daily Summary</div>
      <div style={{ fontSize: 13, color: theme.gray400, fontWeight: 600, marginBottom: 18 }}>{dateLabel}</div>

      <DateFilterBar selectedDate={selectedDate} onDateChange={setSelectedDate} />

      {loading ? (
        <Loader text="Loading summary..." />
      ) : (
        <>
          {(salesStats.length > 0 || hasShiftData) && (
            <StatGroup
              title="💰 Sales & Financials"
              cards={[...salesStats, ...financialCards]}
            />
          )}

          {!hasShiftData && (
            <div style={{ background: theme.gray50, borderRadius: 16, padding: "24px", textAlign: "center", marginBottom: 22, color: theme.gray400, fontSize: 13, fontWeight: 600 }}>
              No shift data available for this date.
            </div>
          )}

          {hasShiftData && (
            <>
              <StatGroup title="💧 RO Production" cards={roCards} />
              <StatGroup title="🪣 Kiosk" cards={kioskCards} />
              <StatGroup title="⚡ Power" cards={powerCards} />
            </>
          )}

          {Array.isArray(products) && products.length > 0 && (
            <>
              <div className="group-header">📦 Top Products</div>
              {products.map((p, i) => (
                <div key={i} style={{ marginBottom: 13 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: theme.gray800 }}>{p?.name ?? "—"}</div>
                    <div style={{ fontWeight: 700, fontSize: 12, color: theme.gray600 }}>KES {p?.revenue ?? 0} · {p?.qty ?? 0} units</div>
                  </div>
                  <div style={{ background: theme.gray100, borderRadius: 100, height: 8, overflow: "hidden" }}>
                    <div style={{
                      width: `${Math.min(p?.pct ?? 0, 100)}%`,
                      background: i === 0 ? theme.green : i === 1 ? theme.blue : theme.yellow,
                      height: "100%", borderRadius: 100, transition: "width 0.6s ease"
                    }} />
                  </div>
                </div>
              ))}
            </>
          )}

          {Array.isArray(products) && products.length === 0 && hasShiftData && (
            <div style={{ textAlign: "center", padding: "16px 0 4px", color: theme.gray400, fontSize: 13, fontWeight: 600 }}>
              No product sales data for this date.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AuditScreen() {
  const today = new Date().toISOString().split('T')[0];
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState("All");
  const [selectedDate, setSelectedDate] = useState(today);

  const typeConfig = {
    sale: { bg: theme.greenLight, color: theme.greenDark, icon: "" },
    expense: { bg: theme.redLight, color: theme.redDark, icon: "" },
    shift: { bg: theme.blueLight, color: theme.blueDark, icon: "" },
    void: { bg: theme.yellowLight, color: theme.yellowDark, icon: "" },
  };

  const filterMap = {
    "All": null,
    "Sales": "sale",
    "Expenses": "expense",
    "Shifts": "shift",
    "Voids": "void",
  };

  const loadAudit = async (date) => {
    setLoading(true);
    try {
      const data = await fetchShiftForDate(date);
      setAuditLog(Array.isArray(data?.audit_log) ? data.audit_log : []);
    } catch (e) {
      console.error("Failed to load audit log:", e);
      setAuditLog([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAudit(selectedDate); }, [selectedDate]);

  const filteredLog = Array.isArray(auditLog)
    ? (filterMap[activeFilter]
        ? auditLog.filter(log => log?.type === filterMap[activeFilter])
        : auditLog)
    : [];

  return (
    <div className="screen slide-up">
      <div style={{ fontSize: 22, fontWeight: 800, color: theme.gray800, marginBottom: 2 }}>Audit Log</div>
      <div style={{ fontSize: 13, color: theme.gray400, fontWeight: 600, marginBottom: 18 }}>All system actions — tamper-proof</div>

      <DateFilterBar selectedDate={selectedDate} onDateChange={setSelectedDate} />

      <div style={{ display: "flex", gap: 8, marginBottom: 18, overflowX: "auto", paddingBottom: 4 }}>
        {Object.keys(filterMap).map(f => (
          <button
            key={f}
            className={`audit-filter-chip${activeFilter === f ? ' active' : ''}`}
            onClick={() => setActiveFilter(f)}
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <Loader text="Loading audit log..." />
      ) : filteredLog.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: theme.gray400 }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}></div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>No {activeFilter !== "All" ? activeFilter.toLowerCase() : ""} entries for this date.</div>
        </div>
      ) : (
        filteredLog.map((log, idx) => {
          const cfg = typeConfig[log?.type] ?? { bg: theme.gray100, color: theme.gray600, icon: "" };
          return (
            <div key={log?.id ?? idx} className="log-row">
              <div className="badge" style={{ background: cfg.bg }}>
                <span style={{ fontSize: 15 }}>{cfg.icon}</span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: theme.gray800 }}>{log?.action ?? "—"}</div>
                <div style={{ fontSize: 11, color: theme.gray400, fontWeight: 600 }}>{log?.detail ?? ""}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: theme.gray600 }}>{log?.time ?? ""}</div>
                <div style={{ fontSize: 11, color: theme.gray400, fontWeight: 600 }}>{log?.user ?? ""}</div>
              </div>
            </div>
          );
        })
      )}

      <div style={{ marginTop: 20, padding: "12px", background: theme.gray50, borderRadius: 12, textAlign: "center" }}>
        <div style={{ fontSize: 11, color: theme.gray400, fontWeight: 700 }}>
          {filteredLog.length} {activeFilter !== "All" ? activeFilter.toLowerCase() : ""} entries · {selectedDate}
        </div>
      </div>
    </div>
  );
}

const NAV = [
  { id: "start", label: "Start" },
  { id: "pos", label: "POS" },
  { id: "expense", label: "Expense" },
  { id: "endday", label: "End Day" },
  { id: "summary", label: "Reports" },
];

export default function WaterPOS() {
  const [screen, setScreen] = useState("start");
  const [shiftStarted, setShiftStarted] = useState(false);
  const navigate = useNavigate();

  const handleNavClick = (navItem) => {
    if (navItem.id === "pos") {
      navigate('/pos');
      return;
    }
    if (navItem.id === "expense") {
      navigate('/manage-expenses');
      return;
    }
    setScreen(navItem.id);
  };

  const renderScreen = () => {
    switch (screen) {
      case "start": return <StartDayScreen />;
      case "pos": return <POSScreen />;
      case "expense": return <ExpenseScreen />;
      case "endday": return <EndDayScreen />;
      case "summary": return <SummaryScreen />;
      case "audit": return <AuditScreen />;
      default: return null;
    }
  };

  return (
    <>
      <style>{styles}</style>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href={QUICKSAND} rel="stylesheet" />
      <div className="app">
        <aside className="app-sidebar">
          <div className="sidebar-logo">
          </div>

          {NAV.map(n => (
            <button
              key={n.id}
              className={`sidebar-nav-item${screen === n.id ? ' active' : ''}`}
              onClick={() => handleNavClick(n)}
            >
              <span><img width="24" height="24" src={NAV_ICONS[n.id]} /></span>
              {n.label}
            </button>
          ))}

          <button
            className={`sidebar-nav-item${screen === 'audit' ? ' active' : ''}`}
            onClick={() => setScreen("audit")}
            style={{ marginTop: 4 }}
          >
            <span><img width="24" height="24" src={NAV_ICONS["audit"]} /></span>
            Audit Log
          </button>

          <div style={{ flex: 1 }} />
          {shiftStarted && (
            <div style={{ margin: "12px 16px", background: theme.greenLight, borderRadius: 10, padding: "8px 12px", display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 8, height: 8, background: theme.green, borderRadius: "50%", flexShrink: 0 }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: theme.greenDark }}>Shift Active</span>
            </div>
          )}
        </aside>

        <div className="app-content">
          <div className="app-header" style={{ background: "#fff", borderBottom: `1.5px solid ${theme.gray100}`, padding: "13px 16px 11px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: theme.gray800 }}>
              {NAV.find(n => n.id === screen)?.label || "Audit Log"}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {shiftStarted && <div className="pill" style={{ background: theme.greenLight, color: theme.greenDark }}>● Shift Active</div>}
              <button
                onClick={() => setScreen("audit")}
                style={{ background: theme.gray100, border: "none", borderRadius: 8, padding: "7px 11px", cursor: "pointer", fontSize: 12, fontFamily: "'Quicksand', sans-serif", fontWeight: 700 }}
                title="Audit Log"
              >
                 Logs
              </button>
            </div>
          </div>

          <div key={screen}>
            {renderScreen()}
          </div>
        </div>

        <nav className="nav-bottom">
          {NAV.map(n => {
            const active = screen === n.id;
            return (
              <button key={n.id} className="nav-item" onClick={() => handleNavClick(n)}>
                <span className="nav-icon"><img width="24" height="24" src={NAV_ICONS[n.id]} /></span>
                <span className="nav-label" style={{ color: active ? theme.green : theme.gray400 }}>{n.label}</span>
                {active && <div style={{ width: 18, height: 3, background: theme.green, borderRadius: 100, marginTop: 1 }} />}
              </button>
            );
          })}
        </nav>
      </div>
    </>
  );
}